/**
 * SoleadLab 2.0 — Scoring Store (D2.4)
 *
 * Zustand store for score data. All values are backend outputs — this store
 * never derives scores, weights, or confidence from raw data.
 * Three-state model: scored / not_scored / no_state.
 */

import { create } from 'zustand';
import type {
  ScoreEntry,
  ScoredEntry,
  NotScoredEntry,
  ListScoresResponse,
  ScoreComponentsResponse,
} from '@/types/scoring';
import * as scoringApi from '@/services/scoringApi';

// ─── Store interface ────────────────────────────────────────────────────────

interface ScoringStore {
  currentSiteId: string | null;

  // All scores for site (from list endpoint)
  siteScores: Record<string, ScoreEntry>;
  assetScoresList: Array<{
    asset_id: string;
    scores: Record<string, ScoredEntry | NotScoredEntry>;
  }>;
  loading: boolean;
  error: string | null;

  // Component drilldown
  selectedScoreSnapshotId: string | null;
  components: ScoreComponentsResponse | null;
  componentsLoading: boolean;

  // Compute state
  computeStatus: 'idle' | 'running' | 'done' | 'error';

  // Actions
  setSiteId: (siteId: string | null) => void;
  loadScores: () => Promise<void>;
  loadComponents: (scoreSnapshotId: string) => Promise<void>;
  clearComponents: () => void;
  computeScores: () => Promise<void>;
}

// ─── Store implementation ───────────────────────────────────────────────────

export const useScoringStore = create<ScoringStore>((set, get) => ({
  currentSiteId: null,

  siteScores: {},
  assetScoresList: [],
  loading: false,
  error: null,

  selectedScoreSnapshotId: null,
  components: null,
  componentsLoading: false,

  computeStatus: 'idle',

  setSiteId: (siteId: string | null) => {
    const prev = get().currentSiteId;
    if (siteId === prev) return;
    set({
      currentSiteId: siteId,
      siteScores: {},
      assetScoresList: [],
      error: null,
      selectedScoreSnapshotId: null,
      components: null,
      computeStatus: 'idle',
    });
    if (siteId) {
      get().loadScores();
    }
  },

  loadScores: async () => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ loading: true, error: null });
    try {
      const data: ListScoresResponse = await scoringApi.listScores(currentSiteId);
      set({
        siteScores: data.site_scores,
        assetScoresList: data.assets,
        loading: false,
      });
    } catch (err: unknown) {
      console.error('Failed to load scores:', err);
      set({ loading: false, error: 'Failed to load scores' });
    }
  },

  loadComponents: async (scoreSnapshotId: string) => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ selectedScoreSnapshotId: scoreSnapshotId, componentsLoading: true });
    try {
      const data = await scoringApi.getScoreComponents(currentSiteId, scoreSnapshotId);
      set({ components: data, componentsLoading: false });
    } catch (err) {
      console.error('Failed to load components:', err);
      set({ componentsLoading: false });
    }
  },

  clearComponents: () => {
    set({ selectedScoreSnapshotId: null, components: null });
  },

  computeScores: async () => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ computeStatus: 'running' });
    try {
      await scoringApi.computeScores(currentSiteId);
      set({ computeStatus: 'done' });
      // Refresh scores after compute
      get().loadScores();
    } catch (err) {
      console.error('Score computation failed:', err);
      set({ computeStatus: 'error' });
    }
  },
}));
